/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  PartnerConfigResponse,
  ProfileListResponse,
  ProfileResponse,
  SelectedProfileResponse,
  TProfileManagementServiceCreateProfileParams,
  TProfileManagementServiceDeleteProfileParams,
  TProfileManagementServiceSetSelectedProfileParams,
  TProfileManagementServiceUpdateProfileParams
} from "@tivocorp/data-domain-lib";

import {
  ProfileManagementInteractor,
  PostSigninInteractor
} from "../../model/interactor";
import { Locale, LocaleKeys } from "../../model/locale";
import { Log } from "../../model/logging";
import { DeviceModel } from "../../model/device";
import {
  StorageAdapter,
  STORAGE_KEY_ACTIVE_PROFILE_SELECTION
} from "../../model/storage";
import type { UnityRootState } from "../UnityAppStore";
import type {
  ProfilesState,
  TActiveProfileSelection,
  TLoadProfilesParams,
  TProfileError
} from "./profiles.types";
import { getImageHostBaseUrl } from "../utility";
import {
  isProfileError,
  isProfileNotFoundError,
  normalizeProfileError
} from "./profileErrorHandling";
import { activateProfile } from "./thunks/profileActivation.thunks";
import { deactivateProfileAuthentication } from "./thunks/profileDeactivation.thunks";
import { loadProfile } from "./thunks/profileLoading.thunks";

const TAG: string = "profilesSlice";
const PROFILE_IMAGE_TYPE: string = "profileImage";
const IMAGE_PATH: string = "/images";
const PROFILE_AVATAR_IMAGE_SIZE: number = 230;

const INITIAL_STATE: ProfilesState = {
  profilesResponse: null,
  selectedProfileResponse: null,
  selectedProfileId: "",
  imageHostBaseUrl: "",
  activeProfileSelection: null,
  isProfileSelected: false,
  isLoadingProfiles: false,
  isLoadingProfile: false,
  isCreatingProfile: false,
  isUpdatingProfile: false,
  isDeletingProfile: false,
  isProfileSelectionRequired: false,
  partnerConfigResponse: null,
  isLoadingPartnerConfig: false,
  isSettingSelectedProfile: false,
  error: "",
  channelLineupRevision: 0
};

/**
 * In-flight `/profile` request shared by all concurrent {@link loadProfiles}
 * dispatches, or null when none is running.
 *
 * Several independent callers ask for profiles on the same post sign-in
 * entry — postSigninStartupSequence's profile gate, MainLayout's profile
 * gate, and SelectProfileScreen's hard-reload fallback — and they overlap
 * because MainLayout keys off `isProfileGateResolved`, which the sequence
 * resets to false while it runs. Sharing the promise collapses them onto a
 * single network call while still giving every caller the response, so no
 * caller has to handle a "skipped" outcome.
 */
let inFlightProfilesRequest: Promise<ProfileListResponse> | null = null;

function getRejectedProfileMessage(
  payload: TProfileError | string | undefined,
  fallbackMessage: string
): string {
  if (typeof payload === "string") {
    return payload;
  }
  if (payload !== undefined) {
    return payload.message;
  }
  return fallbackMessage;
}

/**
 * Fetches the profile list, reusing an already in-flight request when one
 * exists unless `forceRefresh` is set. The shared request is released as soon
 * as it settles, so a later dispatch always issues a fresh call.
 */
async function fetchProfilesDeduped(
  signal: AbortSignal,
  forceRefresh: boolean
): Promise<ProfileListResponse> {
  if (!forceRefresh && inFlightProfilesRequest !== null) {
    Log.d(TAG, "fetchProfilesDeduped() reusing in-flight /profile request");
    return inFlightProfilesRequest;
  }

  const interactor: ProfileManagementInteractor =
    new ProfileManagementInteractor();
  const request: Promise<ProfileListResponse> = interactor.fetchAllProfiles(
    {},
    // Session-scoped data: the post sign-in redirect chain
    // (/signin → /home → /select-profile) must not abort it.
    { signal, bypassNavigationCancellation: true }
  );
  inFlightProfilesRequest = request;

  try {
    return await request;
  } finally {
    // A forced refresh may have replaced this request while it was running;
    // only release the slot if it still holds ours.
    if (inFlightProfilesRequest === request) {
      inFlightProfilesRequest = null;
    }
  }
}

function hasProfile(profiles: ProfileResponse[], profileId: string): boolean {
  return profiles.some(
    (profile: ProfileResponse): boolean => profile.profileId === profileId
  );
}

async function clearDeletedActiveProfileSelection(): Promise<void> {
  try {
    await StorageAdapter.getInstance().delete(
      STORAGE_KEY_ACTIVE_PROFILE_SELECTION
    );
  } catch (selectionError: unknown) {
    Log.e(TAG, "deleteProfile() selection cleanup", selectionError);
  }
  DeviceModel.getTiVoSettings().setProfileId("");
}

export const loadProfiles = createAsyncThunk<
  ProfileListResponse,
  TLoadProfilesParams | void,
  { rejectValue: TProfileError | string }
>(
  "profiles/loadProfiles",
  async (params, { dispatch, rejectWithValue, signal }) => {
    try {
      Log.d(TAG, "loadProfiles() called");
      const forceRefresh: boolean = params !== undefined && params.forceRefresh;
      const profilesResponse = await fetchProfilesDeduped(signal, forceRefresh);

      let imageHostBaseUrl: string = "";
      try {
        imageHostBaseUrl = await getImageHostBaseUrl(
          new PostSigninInteractor()
        );
      } catch (imageHostError: unknown) {
        Log.e(TAG, "loadProfiles() imageHostBaseUrl", imageHostError);
      }

      if (imageHostBaseUrl.length > 0) {
        dispatch(setProfilesImageHostBaseUrl(imageHostBaseUrl));
      }

      return profilesResponse;
    } catch (error: unknown) {
      Log.e(TAG, "loadProfiles()", error);
      return rejectWithValue(
        normalizeProfileError(
          error,
          Locale.getString(LocaleKeys.PROFILE_LOAD_FAILED_FALLBACK)
        )
      );
    }
  }
);

export const loadActiveProfileSelectionPreference = createAsyncThunk<
  TActiveProfileSelection | null,
  void,
  { rejectValue: TProfileError | string }
>(
  "profiles/loadActiveProfileSelectionPreference",
  async (_: void, { rejectWithValue }) => {
    try {
      Log.d(TAG, "loadActiveProfileSelectionPreference() called");
      const storage: StorageAdapter = StorageAdapter.getInstance();
      const result = await storage.get<TActiveProfileSelection>(
        STORAGE_KEY_ACTIVE_PROFILE_SELECTION
      );
      if (result !== null && result !== undefined) {
        DeviceModel.getTiVoSettings().setProfileId(result.activeProfileId);
      }
      return result;
    } catch (error: unknown) {
      Log.e(TAG, "loadActiveProfileSelectionPreference()", error);
      return rejectWithValue(
        normalizeProfileError(
          error,
          Locale.getString(
            LocaleKeys.PROFILE_SELECTION_PREFERENCE_LOAD_FAILED_FALLBACK
          )
        )
      );
    }
  }
);

export const saveActiveProfileSelectionPreference = createAsyncThunk<
  TActiveProfileSelection,
  TActiveProfileSelection,
  { rejectValue: TProfileError | string }
>(
  "profiles/saveActiveProfileSelectionPreference",
  async (params, { rejectWithValue }) => {
    try {
      Log.d(TAG, "saveActiveProfileSelectionPreference() called");
      const storage: StorageAdapter = StorageAdapter.getInstance();
      await storage.set(
        STORAGE_KEY_ACTIVE_PROFILE_SELECTION,
        params,
        Number.POSITIVE_INFINITY
      );
      DeviceModel.getTiVoSettings().setProfileId(params.activeProfileId);
      return params;
    } catch (error: unknown) {
      Log.e(TAG, "saveActiveProfileSelectionPreference()", error);
      return rejectWithValue(
        normalizeProfileError(
          error,
          Locale.getString(
            LocaleKeys.PROFILE_SELECTION_PREFERENCE_SAVE_FAILED_FALLBACK
          )
        )
      );
    }
  }
);

export const loadPartnerConfig = createAsyncThunk<
  PartnerConfigResponse,
  void,
  { rejectValue: TProfileError | string }
>(
  "profiles/loadPartnerConfig",
  async (_: void, { rejectWithValue, signal }) => {
    try {
      Log.d(TAG, "loadPartnerConfig() called");
      const interactor: ProfileManagementInteractor =
        new ProfileManagementInteractor();
      return await interactor.fetchPartnerConfig({}, { signal });
    } catch (error: unknown) {
      Log.e(TAG, "loadPartnerConfig()", error);
      return rejectWithValue(
        normalizeProfileError(
          error,
          Locale.getString(
            LocaleKeys.PROFILE_PARTNER_CONFIG_LOAD_FAILED_FALLBACK
          )
        )
      );
    }
  }
);

export const createProfile = createAsyncThunk<
  ProfileResponse,
  TProfileManagementServiceCreateProfileParams,
  { rejectValue: TProfileError | string }
>("profiles/createProfile", async (params, thunkApi) => {
  const { dispatch, rejectWithValue, signal } = thunkApi;
  try {
    Log.d(TAG, "createProfile() called");
    const interactor: ProfileManagementInteractor =
      new ProfileManagementInteractor();
    const createdProfile: ProfileResponse = await interactor.createProfile(
      params,
      { signal }
    );
    // Profiles live at the account level, so another device may have
    // created, renamed or removed one since this device last fetched —
    // re-read the full list from PMS rather than patching in only this
    // device's change. A failed refresh is already handled by loadProfiles
    // and must not fail the mutation that succeeded.
    await dispatch(loadProfiles({ forceRefresh: true }));
    return createdProfile;
  } catch (error: unknown) {
    Log.e(TAG, "createProfile()", error);
    return rejectWithValue(
      normalizeProfileError(
        error,
        Locale.getString(LocaleKeys.PROFILE_CREATE_FAILED_FALLBACK)
      )
    );
  }
});

export const updateProfile = createAsyncThunk<
  ProfileResponse,
  TProfileManagementServiceUpdateProfileParams,
  { rejectValue: TProfileError | string }
>("profiles/updateProfile", async (params, thunkApi) => {
  const { dispatch, rejectWithValue, signal } = thunkApi;
  try {
    Log.d(TAG, "updateProfile() called");
    const interactor: ProfileManagementInteractor =
      new ProfileManagementInteractor();
    const updatedProfile: ProfileResponse = await interactor.updateProfile(
      params,
      { signal }
    );
    // Profiles live at the account level, so another device may have
    // created, renamed or removed one since this device last fetched —
    // re-read the full list from PMS rather than patching in only this
    // device's change. A failed refresh is already handled by loadProfiles
    // and must not fail the mutation that succeeded.
    await dispatch(loadProfiles({ forceRefresh: true }));
    return updatedProfile;
  } catch (error: unknown) {
    Log.e(TAG, "updateProfile()", error);
    if (isProfileError(error) && isProfileNotFoundError(error)) {
      await dispatch(deactivateProfileAuthentication(params.profileId));
    }
    return rejectWithValue(
      normalizeProfileError(
        error,
        Locale.getString(LocaleKeys.PROFILE_UPDATE_FAILED_FALLBACK)
      )
    );
  }
});

export const deleteProfile = createAsyncThunk<
  void,
  TProfileManagementServiceDeleteProfileParams,
  {
    state: { profiles: ProfilesState };
    rejectValue: TProfileError | string;
  }
>("profiles/deleteProfile", async (params, thunkApi) => {
  const { dispatch, getState, rejectWithValue, signal } = thunkApi;
  try {
    Log.d(TAG, "deleteProfile() called");
    const interactor: ProfileManagementInteractor =
      new ProfileManagementInteractor();
    await interactor.deleteProfile(params, { signal });
    await dispatch(deactivateProfileAuthentication(params.profileId));
    const isSelectedProfile: boolean =
      getState().profiles.selectedProfileId === params.profileId;
    if (isSelectedProfile) {
      await clearDeletedActiveProfileSelection();
    }
  } catch (error: unknown) {
    Log.e(TAG, "deleteProfile()", error);
    if (isProfileError(error) && isProfileNotFoundError(error)) {
      await dispatch(deactivateProfileAuthentication(params.profileId));
    }
    return rejectWithValue(
      normalizeProfileError(
        error,
        Locale.getString(LocaleKeys.PROFILE_DELETE_FAILED_FALLBACK)
      )
    );
  }
});

export const setSelectedProfile = createAsyncThunk<
  SelectedProfileResponse,
  TProfileManagementServiceSetSelectedProfileParams,
  { rejectValue: TProfileError | string }
>(
  "profiles/setSelectedProfile",
  async (params, { rejectWithValue, signal }) => {
    try {
      Log.d(TAG, "setSelectedProfile() called");
      const interactor: ProfileManagementInteractor =
        new ProfileManagementInteractor();
      return await interactor.setSelectedProfile(params, { signal });
    } catch (error: unknown) {
      Log.e(TAG, "setSelectedProfile()", error);
      return rejectWithValue(
        normalizeProfileError(
          error,
          Locale.getString(LocaleKeys.PROFILE_SET_SELECTED_FAILED_FALLBACK)
        )
      );
    }
  }
);

const profilesSlice = createSlice({
  name: "profiles",
  initialState: INITIAL_STATE,
  reducers: {
    setSelectedProfileId(state, action: PayloadAction<string>): void {
      state.selectedProfileId = action.payload;
    },
    setImageHostBaseUrl(state, action: PayloadAction<string>): void {
      state.imageHostBaseUrl = action.payload;
    },
    setIsProfileSelected(state, action: PayloadAction<boolean>): void {
      state.isProfileSelected = action.payload;
    },
    setIsProfileSelectionRequired(state, action: PayloadAction<boolean>): void {
      state.isProfileSelectionRequired = action.payload;
    },
    clearProfilesError(state): void {
      state.error = "";
    },
    channelLineupRefreshed(state): void {
      state.channelLineupRevision += 1;
    },
    resetProfilesState(): ProfilesState {
      return INITIAL_STATE;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadProfiles.pending, (state) => {
      state.isLoadingProfiles = true;
      state.error = "";
    });
    builder.addCase(loadProfiles.fulfilled, (state, action) => {
      state.isLoadingProfiles = false;
      state.profilesResponse = action.payload;
    });
    builder.addCase(loadProfiles.rejected, (state, action) => {
      state.isLoadingProfiles = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_LOAD_FAILED_FALLBACK)
      );
    });

    builder.addCase(loadProfile.pending, (state) => {
      state.isLoadingProfile = true;
      state.error = "";
    });
    builder.addCase(loadProfile.fulfilled, (state, action) => {
      state.isLoadingProfile = false;
      state.selectedProfileResponse = action.payload;
    });
    builder.addCase(loadProfile.rejected, (state, action) => {
      state.isLoadingProfile = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_FETCH_FAILED_FALLBACK)
      );
    });

    builder.addCase(loadPartnerConfig.pending, (state) => {
      state.isLoadingPartnerConfig = true;
      state.error = "";
    });
    builder.addCase(loadPartnerConfig.fulfilled, (state, action) => {
      state.isLoadingPartnerConfig = false;
      state.partnerConfigResponse = action.payload;
    });
    builder.addCase(loadPartnerConfig.rejected, (state, action) => {
      state.isLoadingPartnerConfig = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_PARTNER_CONFIG_LOAD_FAILED_FALLBACK)
      );
    });

    builder.addCase(createProfile.pending, (state) => {
      state.isCreatingProfile = true;
      state.error = "";
    });
    builder.addCase(createProfile.fulfilled, (state, action) => {
      state.isCreatingProfile = false;
      state.selectedProfileResponse = action.payload;
      // The post-create refresh normally already contains the new profile;
      // only append it when that refresh failed or was not yet consistent.
      if (
        state.profilesResponse !== null &&
        state.profilesResponse.profiles !== null &&
        state.profilesResponse.profiles !== undefined &&
        !hasProfile(state.profilesResponse.profiles, action.payload.profileId)
      ) {
        state.profilesResponse.profiles.push(action.payload);
      }
    });
    builder.addCase(createProfile.rejected, (state, action) => {
      state.isCreatingProfile = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_CREATE_FAILED_FALLBACK)
      );
    });

    builder.addCase(updateProfile.pending, (state) => {
      state.isUpdatingProfile = true;
      state.error = "";
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.isUpdatingProfile = false;
      state.selectedProfileResponse = action.payload;
      if (
        state.profilesResponse !== null &&
        state.profilesResponse.profiles !== null &&
        state.profilesResponse.profiles !== undefined
      ) {
        const profileIndex: number = state.profilesResponse.profiles.findIndex(
          (profile: ProfileResponse): boolean =>
            profile.profileId === action.payload.profileId
        );
        if (profileIndex >= 0) {
          state.profilesResponse.profiles[profileIndex] = action.payload;
        }
      }
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.isUpdatingProfile = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_UPDATE_FAILED_FALLBACK)
      );
    });

    builder.addCase(deleteProfile.pending, (state) => {
      state.isDeletingProfile = true;
      state.error = "";
    });
    builder.addCase(deleteProfile.fulfilled, (state, action) => {
      state.isDeletingProfile = false;
      if (state.selectedProfileId === action.meta.arg.profileId) {
        state.selectedProfileResponse = null;
        state.selectedProfileId = "";
        state.activeProfileSelection = null;
        state.isProfileSelected = false;
      }
    });
    builder.addCase(deleteProfile.rejected, (state, action) => {
      state.isDeletingProfile = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_DELETE_FAILED_FALLBACK)
      );
    });

    builder.addCase(setSelectedProfile.pending, (state) => {
      state.isSettingSelectedProfile = true;
      state.error = "";
    });
    builder.addCase(setSelectedProfile.fulfilled, (state) => {
      state.isSettingSelectedProfile = false;
    });
    builder.addCase(setSelectedProfile.rejected, (state, action) => {
      state.isSettingSelectedProfile = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_SET_SELECTED_FAILED_FALLBACK)
      );
    });

    builder.addCase(activateProfile.pending, (state) => {
      state.isSettingSelectedProfile = true;
      state.error = "";
    });
    builder.addCase(activateProfile.fulfilled, (state, action) => {
      state.isSettingSelectedProfile = false;
      state.selectedProfileId = action.payload.selection.activeProfileId;
      state.activeProfileSelection = action.payload.selection;
      state.isProfileSelected = true;
    });
    builder.addCase(activateProfile.rejected, (state, action) => {
      state.isSettingSelectedProfile = false;
      state.error = getRejectedProfileMessage(
        action.payload,
        Locale.getString(LocaleKeys.PROFILE_ACTIVATE_FAILED_FALLBACK)
      );
    });

    builder.addCase(
      loadActiveProfileSelectionPreference.fulfilled,
      (state, action) => {
        state.activeProfileSelection = action.payload;
      }
    );
    builder.addCase(
      saveActiveProfileSelectionPreference.fulfilled,
      (state, action) => {
        state.activeProfileSelection = action.payload;
      }
    );
  }
});

export const {
  setSelectedProfileId,
  clearProfilesError,
  resetProfilesState,
  setIsProfileSelected,
  setIsProfileSelectionRequired,
  channelLineupRefreshed
} = profilesSlice.actions;

export { activateProfile, loadProfile };

export const setProfilesImageHostBaseUrl =
  profilesSlice.actions.setImageHostBaseUrl;

export const selectProfilesResponse = (
  state: UnityRootState
): ProfileListResponse | null => state.profiles.profilesResponse;

export const selectSelectedProfileResponse = (
  state: UnityRootState
): ProfileResponse | null => state.profiles.selectedProfileResponse;

export const selectPartnerConfigResponse = (
  state: UnityRootState
): PartnerConfigResponse | null => state.profiles.partnerConfigResponse;

export const selectIsLoadingPartnerConfig = (state: UnityRootState): boolean =>
  state.profiles.isLoadingPartnerConfig;

export const selectSelectedProfileId = (state: UnityRootState): string =>
  state.profiles.selectedProfileId;

export const selectChannelLineupRevision = (state: UnityRootState): number =>
  state.profiles.channelLineupRevision;

export const selectProfilesImageHostBaseUrl = (state: UnityRootState): string =>
  state.profiles.imageHostBaseUrl;

export const selectActiveProfileSelection = (
  state: UnityRootState
): TActiveProfileSelection | null => state.profiles.activeProfileSelection;

export const selectIsProfileSelected = (state: UnityRootState): boolean =>
  state.profiles.isProfileSelected;

export const selectIsLoadingProfiles = (state: UnityRootState): boolean =>
  state.profiles.isLoadingProfiles;

export const selectIsLoadingProfile = (state: UnityRootState): boolean =>
  state.profiles.isLoadingProfile;

export const selectIsCreatingProfile = (state: UnityRootState): boolean =>
  state.profiles.isCreatingProfile;

export const selectIsUpdatingProfile = (state: UnityRootState): boolean =>
  state.profiles.isUpdatingProfile;

export const selectIsDeletingProfile = (state: UnityRootState): boolean =>
  state.profiles.isDeletingProfile;

export const selectIsProfileSelectionRequired = (
  state: UnityRootState
): boolean => state.profiles.isProfileSelectionRequired;

export const selectIsSettingSelectedProfile = (
  state: UnityRootState
): boolean => state.profiles.isSettingSelectedProfile;

export const selectProfilesError = (state: UnityRootState): string =>
  state.profiles.error;

/**
 * True when the account has reached the partner's maxProfiles limit — the
 * Manage Profiles screen swaps the "Add New Profile" affordance for a
 * limit-reached message in that case. Undetermined (partner config not yet
 * loaded, or maxProfiles absent from the response) always reports false, so
 * the affordance stays visible until proven otherwise.
 */
export const selectHasReachedProfileLimit = (
  state: UnityRootState
): boolean => {
  const profilesResponse: ProfileListResponse | null =
    state.profiles.profilesResponse;
  const profiles: ProfileResponse[] =
    profilesResponse !== null &&
    profilesResponse.profiles !== null &&
    profilesResponse.profiles !== undefined
      ? profilesResponse.profiles
      : [];

  const partnerConfigResponse: PartnerConfigResponse | null =
    state.profiles.partnerConfigResponse;
  const maxProfiles: number | undefined =
    partnerConfigResponse !== null &&
    partnerConfigResponse.attributes !== null &&
    partnerConfigResponse.attributes !== undefined
      ? partnerConfigResponse.attributes.maxProfiles
      : undefined;

  if (maxProfiles === undefined) {
    return false;
  }

  return profiles.length >= maxProfiles;
};

/**
 * True when the account's Admin profile has never been edited and there is
 * only one profile — the Settings screen shows "Create Profile" instead of
 * "Manage Profiles" in that case.
 */
export const selectShouldShowCreateProfileOption = (
  state: UnityRootState
): boolean => {
  const profilesResponse: ProfileListResponse | null =
    state.profiles.profilesResponse;
  const profiles: ProfileResponse[] =
    profilesResponse !== null &&
    profilesResponse.profiles !== null &&
    profilesResponse.profiles !== undefined
      ? profilesResponse.profiles
      : [];

  if (profiles.length > 1) {
    return false;
  }

  const adminProfile: ProfileResponse | undefined = profiles.find(
    (profile: ProfileResponse): boolean => profile.isAdmin === true
  );

  if (adminProfile === undefined) {
    return false;
  }

  return adminProfile.createdAt === adminProfile.updatedAt;
};

export const buildProfileAvatarImageUrl = (
  imageHostBaseUrl: string,
  avatarId: string
): string => {
  if (imageHostBaseUrl.length === 0 || avatarId.length === 0) {
    return "";
  }

  const normalizedBaseUrl: string = imageHostBaseUrl.endsWith("/")
    ? imageHostBaseUrl.slice(0, -1)
    : imageHostBaseUrl;

  const params: URLSearchParams = new URLSearchParams({
    objectid: avatarId,
    imageType: PROFILE_IMAGE_TYPE,
    width: String(PROFILE_AVATAR_IMAGE_SIZE),
    height: String(PROFILE_AVATAR_IMAGE_SIZE)
  });

  return normalizedBaseUrl + IMAGE_PATH + "?" + params.toString();
};

export default profilesSlice.reducer;
